import { Gutter } from "@/features/admin/features/pages-management/utils/gutters";
import {
  WIDGET_GRID_COLS,
  WIDGET_GRID_MARGIN,
  WIDGET_GRID_ROW_HEIGHT,
} from "@/features/shared/components/widget-grid-layout/widget-grid-layout";
import { calcColumnWidth } from "@/features/admin/features/pages-management/utils/grid-resize-math";

import styles from "./gutter-handles.module.css";

type Props = {
  gutters: Gutter[];
  containerWidth: number;
  activeGutterId: string | null;
  suppressInteraction?: boolean;
  onResizeStart: (gutter: Gutter, event: React.PointerEvent<HTMLElement>) => void;
};

function gutterStyle(
  gutter: Gutter,
  columnWidth: number,
  marginX: number,
  marginY: number,
): React.CSSProperties {
  if (gutter.orientation === "vertical") {
    return {
      left: gutter.boundary * columnWidth + (gutter.boundary - 1) * marginX,
      width: marginX,
      top: gutter.start * (WIDGET_GRID_ROW_HEIGHT + marginY),
      height: (gutter.end - gutter.start) * (WIDGET_GRID_ROW_HEIGHT + marginY) - marginY,
    };
  }

  return {
    top: gutter.boundary * WIDGET_GRID_ROW_HEIGHT + (gutter.boundary - 1) * marginY,
    height: marginY,
    left: gutter.start * (columnWidth + marginX),
    width: (gutter.end - gutter.start) * (columnWidth + marginX) - marginX,
  };
}

export function GutterHandles({
  gutters,
  containerWidth,
  activeGutterId,
  suppressInteraction = false,
  onResizeStart,
}: Props) {
  if (containerWidth <= 0) return null;

  const [marginX, marginY] = WIDGET_GRID_MARGIN;
  const columnWidth = calcColumnWidth(containerWidth, WIDGET_GRID_COLS, marginX);

  return (
    <div
      className={`${styles.layer} ${suppressInteraction ? styles.suppressInteraction : ""}`}
      aria-hidden
    >
      {gutters.map((gutter) => {
        const isActive = gutter.id === activeGutterId;

        return (
          <div
            key={gutter.id}
            data-no-drag
            role="separator"
            aria-orientation={gutter.orientation}
            className={`${styles.handle} ${
              gutter.orientation === "vertical" ? styles.vertical : styles.horizontal
            } ${isActive ? styles.active : ""}`}
            style={gutterStyle(gutter, columnWidth, marginX, marginY)}
            onPointerDown={(event) => onResizeStart(gutter, event)}
          />
        );
      })}
    </div>
  );
}
